import React from 'react';
import {
  Expressions,
  type Component,
  type EvalEnv,
  type ExpressionPath,
  type Plugins,
} from '@packages/systems/wfdl';
import type {ExpressionElement} from '@packages/systems/wfdl-ast';
import {
  EvaluatedNodeData,
  findChild,
  getChildren,
  getInPojo,
} from '@packages/utilities/data-utils';
import {isSlot} from '@packages/utilities/elementTypeUtils';
import {isNodePojo} from '@packages/systems/core/nodepojo';
import {nodePojoToElementWithComponents} from '@packages/systems/core/component-conversions';
import {FluxContext} from '@packages/systems/flux';
import type {DesignerFlux} from '@packages/systems/designer/flux';
import {
  computeNativeId,
  getGuid,
  getInstanceIndicesFromNativeId,
} from '@packages/systems/core/utils/NativeIdUtils';
import {useDispatch} from '@packages/systems/render';
import {
  POPOVER_ANIMATION_OPEN_COMMAND,
  POPOVER_ANIMATION_CLOSE_COMMAND,
} from '@packages/systems/core-constants';
import {OVERLAY_SHOWN} from '@packages/systems/designer/actions';
import {
  POPOVER_GHOST_ANCHOR_ATTRIBUTE,
  POPOVER_GHOST_SOURCED_ATTRIBUTE,
  listenForPopoverCommands,
  type PopoverElementWithToolbarSource,
  type PopoverCommand,
} from '@packages/systems/plugins-utils/Popover';

const useIsomorphicLayoutEffect =
  typeof window === 'undefined' ? React.useEffect : React.useLayoutEffect;

export type OverlayElement = PopoverElementWithToolbarSource & {
  // Method syntax so the element stays assignable from `HTMLDialogElement`,
  // whose native `showPopover` takes an `HTMLElement` source.
  showPopover?(options?: {source?: Element}): void;
  hidePopover?(): void;
};

// `none` shows the overlay without a trigger source, centered by the browser.
// On Open Canvas it still uses the frame's ghost anchor to position itself.
type OverlayAnchorStrategy = 'trigger' | 'none';

type OverlayController = {
  selectionShow: (source?: Element, onReady?: () => void) => boolean;
  selectionHide: () => void;
  registerDescendant: (hide: () => void) => () => void;
};

const OverlayOwnershipContext = React.createContext<OverlayController | null>(
  null
);

const isPopoverSelectedOrDescendant = (
  data: EvaluatedNodeData,
  elementId: string,
  selectedNativeIdPath: readonly string[]
) => {
  const selectedGuids = selectedNativeIdPath.map(getGuid);

  return selectedGuids.some(
    (selectedGuid) =>
      selectedGuid === elementId ||
      Boolean(
        findChild(data, (childElement: ExpressionElement) =>
          Boolean(
            childElement.id === selectedGuid ||
            (childElement.data &&
              Expressions.getElementById(childElement.data, selectedGuid))
          )
        )
      )
  );
};

type SlotHostEnv = {
  evalEnv: EvalEnv.EvalEnvType;
  nativeIdPath: readonly string[];
  plugins: Plugins.PluginsType;
  components: Component.ComponentMap;
};

const slotChildrenPath: ExpressionPath = [{in: 'Record', at: 'children'}];

const isElementSelectedInScope = (
  element: ExpressionElement,
  scopeNativeIdPath: readonly string[],
  selectedNativeIdPath: readonly string[]
) => {
  const selectedNativeId = selectedNativeIdPath[scopeNativeIdPath.length];
  if (
    selectedNativeId === undefined ||
    !scopeNativeIdPath.every(
      (nativeId, index) => nativeId === selectedNativeIdPath[index]
    )
  ) {
    return false;
  }
  const selectedGuid = getGuid(selectedNativeId);
  return (
    element.id === selectedGuid ||
    Boolean(
      element.data && Expressions.getElementById(element.data, selectedGuid)
    )
  );
};

// Slot content is authored outside the overlay: overrides live in the parent
// instance's data and keep that outer native scope, while defaults come from
// the component definition and render in the Slot's own scope. Neither shows
// up in the overlay's own element tree, so resolve each Slot's content from
// the environment it was evaluated in.
const isSlotContentSelected = (
  children: readonly EvaluatedNodeData[],
  selectedNativeIdPath: readonly string[]
): boolean =>
  children.some((child) => {
    const element = getInPojo(child, ['props', 'element']) as
      | ExpressionElement
      | undefined;
    const env = getInPojo(child, ['props', 'env']) as SlotHostEnv | undefined;
    if (!element || !env?.evalEnv) {
      return false;
    }

    const slots = [
      element,
      ...Object.values(Expressions.allElements(element.data)),
    ].filter(isSlot);

    return slots.some((slot) => {
      const slotChildren = Expressions.getIn(
        Expressions.getElementData(slot),
        slotChildrenPath
      );
      const select = slotChildren && Expressions.getSelect(slotChildren);
      if (!select || !Expressions.isDataVariable(select.from)) {
        return false;
      }
      const content: unknown = Expressions.evaluate(env.evalEnv, slotChildren);
      if (!Array.isArray(content)) {
        return false;
      }

      return content.some((item: unknown) => {
        const itemElement = getInPojo(item, ['props', 'element']) as
          | ExpressionElement
          | undefined;
        const itemEnv = getInPojo(item, ['props', 'env']) as
          | SlotHostEnv
          | undefined;
        if (itemElement && itemEnv) {
          return (
            isElementSelectedInScope(
              itemElement,
              itemEnv.nativeIdPath,
              selectedNativeIdPath
            ) || isSlotContentSelected([item], selectedNativeIdPath)
          );
        }
        if (isNodePojo(item) && typeof item.type === 'string') {
          return isElementSelectedInScope(
            nodePojoToElementWithComponents(env.plugins)(env.components)(item),
            env.nativeIdPath,
            selectedNativeIdPath
          );
        }
        return false;
      });
    });
  });

const isPopoverSelectedForPath = (
  data: EvaluatedNodeData,
  elementId: string,
  popoverNativeIdPath: readonly string[],
  selectedNativeIdPath: readonly string[]
) => {
  const isInSameNativeScope =
    popoverNativeIdPath.length === 0 ||
    popoverNativeIdPath
      .slice(0, -1)
      .every((nativeId, index) => nativeId === selectedNativeIdPath[index]);
  const popoverInstanceIndices = getInstanceIndicesFromNativeId(
    popoverNativeIdPath[popoverNativeIdPath.length - 1] ?? ''
  );
  const selectedInstanceIndices = getInstanceIndicesFromNativeId(
    selectedNativeIdPath[selectedNativeIdPath.length - 1] ?? ''
  );
  const isInSameRepeatingInstance = popoverInstanceIndices.every(
    (index, position) => index === selectedInstanceIndices[position]
  );

  return (
    ((isPopoverSelectedOrDescendant(data, elementId, selectedNativeIdPath) &&
      isInSameNativeScope) ||
      isSlotContentSelected(getChildren(data), selectedNativeIdPath)) &&
    isInSameRepeatingInstance
  );
};

const hidePopoverSafely = (popover: OverlayElement | null) => {
  try {
    popover?.hidePopover?.();
  } catch {
    // Older browsers and unsupported native Popover implementations fail safely.
  }
};

// Showing a `<dialog>` overlay runs the dialog focusing steps, and a Popover
// with an `autofocus` child moves focus the same way. On the Designer canvas
// the overlay lives inside the canvas iframe while the Designer's keyboard
// shortcuts listen on the host Designer page (the iframe's parent), so the
// focus move has to be undone right after the show. When the overlay is not
// framed, or its frame is cross-origin (where `frameElement` is `null`
// instead of throwing), there is no host page to restore and this is a
// pass-through.
const showPopoverKeepingHostPageFocus = (
  element: OverlayElement,
  show: () => void
) => {
  const view = element.ownerDocument.defaultView;
  const hostDocument = view?.frameElement?.ownerDocument ?? null;
  const previous = hostDocument?.activeElement ?? null;

  show();

  const focusedElement = element.ownerDocument.activeElement;
  if (
    !hostDocument ||
    !view ||
    !(focusedElement instanceof view.HTMLElement) ||
    !element.contains(focusedElement)
  ) {
    return;
  }
  focusedElement.blur();
  const hostView = hostDocument.defaultView;
  // Focusing `body` does nothing, so an unfocused host page (or one whose
  // focus sat on the canvas iframe itself) has to fall back to focusing the
  // host window.
  if (
    hostView &&
    previous instanceof hostView.HTMLElement &&
    previous !== view.frameElement &&
    previous !== hostDocument.body &&
    previous.isConnected
  ) {
    previous.focus({preventScroll: true});
  } else {
    hostView?.focus();
  }
};

const POPOVER_TRIGGER_SELECTOR = '.w-popover-trigger[popovertarget]';
const POPOVER_GHOST_ANCHORED_ATTRIBUTE = 'data-wf-popover-ghost-anchored';
const OPEN_CANVAS_FRAME_SELECTOR = '[data-wf-frame-id]';
const OPEN_CANVAS_CONTAINMENT_PROPERTIES = [
  'clip',
  'max-width',
  'max-height',
  'min-width',
  'min-height',
] as const;

type OpenCanvasContainmentProperty =
  (typeof OPEN_CANVAS_CONTAINMENT_PROPERTIES)[number];
type InlineDeclaration = {value: string; priority: string};
type SubscribeToOpenCanvasContainmentUpdates = (
  callback: () => void
) => () => void;

const isVisibleInViewport = (
  element: HTMLElement,
  clippedBounds?: DOMRect
): boolean => {
  const view = element.ownerDocument.defaultView;
  if (!view) return false;

  for (
    let ancestor: HTMLElement | null = element;
    ancestor;
    ancestor = ancestor.parentElement
  ) {
    const styles = view.getComputedStyle(ancestor);
    if (
      styles.display === 'none' ||
      styles.visibility === 'hidden' ||
      styles.visibility === 'collapse' ||
      styles.opacity === '0' ||
      styles.contentVisibility === 'hidden'
    ) {
      return false;
    }
  }

  const bounds = element.getBoundingClientRect();
  const right = Math.min(
    bounds.right,
    view.innerWidth,
    clippedBounds?.right ?? view.innerWidth
  );
  const bottom = Math.min(
    bounds.bottom,
    view.innerHeight,
    clippedBounds?.bottom ?? view.innerHeight
  );
  const left = Math.max(bounds.left, clippedBounds?.left ?? 0, 0);
  const top = Math.max(bounds.top, clippedBounds?.top ?? 0, 0);
  return bounds.width > 0 && bounds.height > 0 && right > left && bottom > top;
};

const findFirstVisiblePopoverTrigger = (
  popover: OverlayElement,
  queryRoot: ParentNode = popover.ownerDocument,
  owningFrame?: HTMLElement
): HTMLElement | null => {
  const triggers = Array.from(
    queryRoot.querySelectorAll<HTMLElement>(POPOVER_TRIGGER_SELECTOR)
  ).filter((trigger) => {
    const action = trigger.getAttribute('popovertargetaction');
    return (
      trigger.getAttribute('popovertarget') === popover.id &&
      action !== 'hide' &&
      !trigger.matches(':disabled') &&
      !popover.contains(trigger) &&
      (!owningFrame ||
        trigger.closest(OPEN_CANVAS_FRAME_SELECTOR) === owningFrame)
    );
  });

  const clippedBounds = owningFrame?.getBoundingClientRect();
  return (
    triggers.find((trigger) => isVisibleInViewport(trigger, clippedBounds)) ??
    triggers[0] ??
    null
  );
};

const createPopoverGhostAnchor = (
  popover: OverlayElement,
  parent?: HTMLElement
): HTMLElement | null => {
  const {ownerDocument} = popover;
  const anchorParent =
    parent ?? ownerDocument.body ?? ownerDocument.documentElement;
  if (!anchorParent) return null;

  const anchor = ownerDocument.createElement('div');
  anchor.setAttribute(POPOVER_GHOST_ANCHOR_ATTRIBUTE, '');
  anchor.setAttribute('aria-hidden', 'true');
  anchor.setAttribute('inert', '');
  // Frame-parented ghosts are absolute so 50%/50% is the frame center. Body
  // ghosts stay fixed so they remain viewport-centered.
  Object.assign(anchor.style, {
    position: parent ? 'absolute' : 'fixed',
    left: '50%',
    top: '50%',
    width: '0',
    height: '0',
    pointerEvents: 'none',
  });
  anchorParent.insertBefore(anchor, anchorParent.firstChild);
  return anchor;
};

// The ghost stays in the DOM for as long as it is the Popover's implicit
// `showPopover()` source, so that switching the Position anchor toggle from
// `None` to `Auto` tethers the Popover without reopening it. Its Designer
// marker would be claiming an anchor the Popover is not using for the whole
// time the toggle sits on `None`, so the marker rides a separate attribute
// gated on what `position-anchor` actually computes to.
//
// `.w-popover` pins `position-anchor: none` from a class selector, which
// outranks the zero-specificity Designer rules, so an unauthored Popover reads
// `none` until the author opts into `Auto`. `normal` only turns up as a
// browser's spelling of the initial value, where it behaves as `none` unless a
// `position-area` consumes it. A named anchor such as `--trigger` is anchored,
// but to that element rather than to the ghost, so it does not earn the marker.
const usesImplicitPopoverAnchor = (popover: OverlayElement): boolean => {
  const view = popover.ownerDocument.defaultView;
  if (!view) return false;

  // Read through `getPropertyValue` so an engine that does not know
  // `position-anchor` reports the empty string rather than `undefined`.
  const styles = view.getComputedStyle(popover);
  const anchor = styles
    .getPropertyValue('position-anchor')
    .trim()
    .toLowerCase();
  if (anchor === 'auto') return true;
  if (anchor !== 'normal') return false;

  const area = styles.getPropertyValue('position-area').trim().toLowerCase();
  return area !== '' && area !== 'none';
};

const containPopoverInOpenCanvasFrame = (
  popover: OverlayElement,
  frame: HTMLElement,
  subscribeToUpdates: SubscribeToOpenCanvasContainmentUpdates
) => {
  const {ownerDocument} = popover;
  const view = ownerDocument.defaultView;
  if (!view) return () => {};

  const readInlineDeclaration = (
    property: OpenCanvasContainmentProperty
  ): InlineDeclaration => ({
    value: popover.style.getPropertyValue(property),
    priority: popover.style.getPropertyPriority(property),
  });
  const authoredDeclarations = new Map<
    OpenCanvasContainmentProperty,
    InlineDeclaration
  >(
    OPEN_CANVAS_CONTAINMENT_PROPERTIES.map((property) => [
      property,
      readInlineDeclaration(property),
    ])
  );
  const containmentDeclarations = new Map<
    OpenCanvasContainmentProperty,
    InlineDeclaration
  >();
  let animationFrame: number | null = null;
  let resizeObserver: ResizeObserver | null = null;
  let mutationObserver: MutationObserver | null = null;
  let unsubscribeFromUpdates: (() => void) | null = null;
  let cleanedUp = false;

  const restoreProperty = (
    property: OpenCanvasContainmentProperty,
    declaration: InlineDeclaration
  ) => {
    if (declaration.value) {
      popover.style.setProperty(
        property,
        declaration.value,
        declaration.priority
      );
    } else {
      popover.style.removeProperty(property);
    }
  };

  const restoreContainment = () => {
    OPEN_CANVAS_CONTAINMENT_PROPERTIES.forEach((property) => {
      const declaration = authoredDeclarations.get(property);
      if (declaration) restoreProperty(property, declaration);
    });
    containmentDeclarations.clear();
  };

  const setContainmentProperty = (
    property: OpenCanvasContainmentProperty,
    value: string
  ) => {
    popover.style.setProperty(property, value, 'important');
    containmentDeclarations.set(property, readInlineDeclaration(property));
  };

  const updateAuthoredDeclarations = () => {
    OPEN_CANVAS_CONTAINMENT_PROPERTIES.forEach((property) => {
      const declaration = readInlineDeclaration(property);
      const containmentDeclaration = containmentDeclarations.get(property);
      if (
        !containmentDeclaration ||
        declaration.value !== containmentDeclaration.value ||
        declaration.priority !== containmentDeclaration.priority
      ) {
        authoredDeclarations.set(property, declaration);
      }
    });
  };

  const round = (value: number) => Math.round(value * 1000) / 1000;

  const intersectAuthoredClip = (
    frameClip: {top: number; right: number; bottom: number; left: number},
    width: number,
    height: number
  ) => {
    const computedClip = view.getComputedStyle(popover).clip;
    if (computedClip === 'auto') return frameClip;

    const match = computedClip.match(
      /^rect\(\s*(auto|-?[\d.]+px)\s*,?\s*(auto|-?[\d.]+px)\s*,?\s*(auto|-?[\d.]+px)\s*,?\s*(auto|-?[\d.]+px)\s*\)$/
    );
    if (!match) return frameClip;
    const [, topValue, rightValue, bottomValue, leftValue] = match;
    if (!topValue || !rightValue || !bottomValue || !leftValue) {
      return frameClip;
    }

    const coordinate = (value: string, autoValue: number) =>
      value === 'auto' ? autoValue : parseFloat(value);
    const authoredClip = {
      top: coordinate(topValue, 0),
      right: coordinate(rightValue, width),
      bottom: coordinate(bottomValue, height),
      left: coordinate(leftValue, 0),
    };

    return {
      top: Math.max(frameClip.top, authoredClip.top),
      right: Math.min(frameClip.right, authoredClip.right),
      bottom: Math.min(frameClip.bottom, authoredClip.bottom),
      left: Math.max(frameClip.left, authoredClip.left),
    };
  };

  const constrainSize = (axis: 'width' | 'height') => {
    const popoverRect = popover.getBoundingClientRect();
    const frameRect = frame.getBoundingClientRect();
    const isWidth = axis === 'width';
    const visualSize = isWidth ? popoverRect.width : popoverRect.height;
    const localBorderBoxSize = isWidth
      ? popover.offsetWidth
      : popover.offsetHeight;
    if (!visualSize || !localBorderBoxSize) return;

    const popoverStart = isWidth ? popoverRect.left : popoverRect.top;
    const popoverEnd = isWidth ? popoverRect.right : popoverRect.bottom;
    const frameStart = isWidth ? frameRect.left : frameRect.top;
    const frameEnd = isWidth ? frameRect.right : frameRect.bottom;
    // A position-area uses the space from its anchor to the viewport edge as
    // its available size. In Open Canvas, intersecting with the owning frame
    // recreates that available area instead of using the combined MCC viewport.
    const visibleSize = Math.max(
      0,
      Math.min(popoverEnd, frameEnd) - Math.max(popoverStart, frameStart)
    );
    if (visibleSize >= visualSize) return;

    const availableBorderBoxSize =
      (visibleSize / visualSize) * localBorderBoxSize;
    const computedStyle = view.getComputedStyle(popover);
    const computedSize = parseFloat(computedStyle[axis]);
    const nonContentSize = Number.isFinite(computedSize)
      ? Math.max(0, localBorderBoxSize - computedSize)
      : 0;
    const availableSize = Math.max(0, availableBorderBoxSize - nonContentSize);
    const maxProperty = isWidth ? 'max-width' : 'max-height';
    const minProperty = isWidth ? 'min-width' : 'min-height';
    const computedMaxSize = parseFloat(
      isWidth ? computedStyle.maxWidth : computedStyle.maxHeight
    );
    const constrainedMaxSize = Number.isFinite(computedMaxSize)
      ? Math.min(computedMaxSize, availableSize)
      : availableSize;
    const computedMinSize = parseFloat(
      isWidth ? computedStyle.minWidth : computedStyle.minHeight
    );

    setContainmentProperty(maxProperty, `${round(constrainedMaxSize)}px`);
    if (Number.isFinite(computedMinSize) && computedMinSize > availableSize) {
      setContainmentProperty(minProperty, `${round(availableSize)}px`);
    }
  };

  const updateContainment = () => {
    animationFrame = null;
    try {
      updateAuthoredDeclarations();
      restoreContainment();
      for (let iteration = 0; iteration < 2; iteration += 1) {
        constrainSize('width');
        constrainSize('height');
      }

      const popoverRect = popover.getBoundingClientRect();
      const frameRect = frame.getBoundingClientRect();
      const width = popover.offsetWidth || popoverRect.width;
      const height = popover.offsetHeight || popoverRect.height;
      if (!popoverRect.width || !popoverRect.height || !width || !height) {
        return;
      }

      const toLocalX = (value: number) => (value / popoverRect.width) * width;
      const toLocalY = (value: number) => (value / popoverRect.height) * height;
      // Keep coordinates outside the Popover border box so shadows and child
      // overflow remain visible until they actually reach the frame boundary.
      const {top, right, bottom, left} = intersectAuthoredClip(
        {
          top: toLocalY(frameRect.top - popoverRect.top),
          right: toLocalX(frameRect.right - popoverRect.left),
          bottom: toLocalY(frameRect.bottom - popoverRect.top),
          left: toLocalX(frameRect.left - popoverRect.left),
        },
        width,
        height
      );

      setContainmentProperty(
        'clip',
        `rect(${round(top)}px, ${round(right)}px, ${round(bottom)}px, ${round(
          left
        )}px)`
      );
    } finally {
      // Containment writes also mutate the style attribute. Discard those
      // records so only authored class/style changes schedule another pass.
      mutationObserver?.takeRecords();
    }
  };

  const scheduleContainmentUpdate = () => {
    if (animationFrame !== null) return;
    animationFrame = view.requestAnimationFrame(updateContainment);
  };

  const cleanup = () => {
    if (cleanedUp) return;
    cleanedUp = true;
    if (animationFrame !== null) {
      view.cancelAnimationFrame(animationFrame);
      animationFrame = null;
    }
    resizeObserver?.disconnect();
    mutationObserver?.disconnect();
    mutationObserver?.takeRecords();
    unsubscribeFromUpdates?.();
    restoreContainment();
  };

  try {
    mutationObserver = view.MutationObserver
      ? new view.MutationObserver(() => {
          updateAuthoredDeclarations();
          scheduleContainmentUpdate();
        })
      : null;
    mutationObserver?.observe(popover, {
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    updateContainment();

    resizeObserver = view.ResizeObserver
      ? new view.ResizeObserver(scheduleContainmentUpdate)
      : null;
    resizeObserver?.observe(popover);
    resizeObserver?.observe(frame);
    unsubscribeFromUpdates = subscribeToUpdates(scheduleContainmentUpdate);
  } catch (error) {
    cleanup();
    throw error;
  }

  return cleanup;
};

const areNativeIdPathsEqual = (
  first: readonly string[],
  second: readonly string[]
) =>
  first.length === second.length &&
  first.every((id, index) => id === second[index]);

const useSelectedNativeIdPath = (
  fallback: readonly string[] | undefined,
  enabled: boolean
) => {
  const flux = React.useContext(FluxContext) as DesignerFlux | null;
  const fallbackKey = fallback?.join('\0');
  const fallbackSnapshot = React.useMemo(() => fallback, [fallbackKey]);
  const subscribe = React.useCallback(
    (onStoreChange: () => void) => {
      if (!enabled || !flux) return () => {};
      return flux.getStore('UiNodeStore').onChange(onStoreChange);
    },
    [enabled, flux]
  );
  const getSnapshot = React.useCallback(
    () =>
      enabled
        ? (flux?.getStoreState('UiNodeStore').selectedNativeIdPath ??
          fallbackSnapshot)
        : fallbackSnapshot,
    [enabled, fallbackSnapshot, flux]
  );

  return React.useSyncExternalStore(
    subscribe,
    getSnapshot,
    () => fallbackSnapshot
  );
};

const useDesignerOverlayVisibility = ({
  data,
  elementId,
  popoverNativeIdPath,
  selectedNativeIdPath,
  designerPopoverRef,
  designerPopoverElement,
  enabled,
  anchorStrategy,
  parentController,
}: {
  data: EvaluatedNodeData;
  elementId: string;
  popoverNativeIdPath: readonly string[];
  selectedNativeIdPath: readonly string[] | undefined;
  designerPopoverRef: React.RefObject<OverlayElement | null>;
  designerPopoverElement: OverlayElement | null;
  enabled: boolean;
  anchorStrategy: OverlayAnchorStrategy;
  parentController: OverlayController | null;
}) => {
  const dispatch = useDispatch();
  const flux = React.useContext(FluxContext) as DesignerFlux | null;
  const forcedClosed = React.useRef(false);
  const [explicitlyOpenedSelection, setExplicitlyOpenedSelection] =
    React.useState<readonly string[] | null>(null);
  const [forcedClosedSelection, setForcedClosedSelection] = React.useState<
    readonly string[] | null
  >(null);
  const skipNextSelectionHide = React.useRef(false);
  const retainedSource = React.useRef<Element | null>(null);
  // Tracks which element `showPopover()` was called on rather than just that it
  // was called. Changing the authored tag swaps the DOM node, and a boolean
  // would survive that swap and suppress the show on the replacement.
  const shownElement = React.useRef<OverlayElement | null>(null);
  const ghostAnchor = React.useRef<HTMLElement | null>(null);
  const descendantHides = React.useRef(new Set<() => void>());
  const [popoverShownVersion, setPopoverShownVersion] = React.useState(0);
  const notifyingReady = React.useRef(false);
  const pendingSelectionShows = React.useRef<Array<() => void>>([]);
  const openCanvasContainmentCleanup = React.useRef<(() => void) | null>(null);
  const selectionShowRef = React.useRef<
    ((source?: Element, onReady?: () => void) => boolean) | null
  >(null);
  const notifyReady = React.useCallback((callback?: () => void) => {
    if (!callback || notifyingReady.current) return;

    notifyingReady.current = true;
    try {
      callback();
    } finally {
      notifyingReady.current = false;
    }
  }, []);
  const clearOpenCanvasContainment = React.useCallback(() => {
    openCanvasContainmentCleanup.current?.();
    openCanvasContainmentCleanup.current = null;
  }, []);
  const subscribeToOpenCanvasContainmentUpdates =
    React.useCallback<SubscribeToOpenCanvasContainmentUpdates>(
      (callback) => {
        if (!flux) return () => {};

        const unsubscribes: Array<() => void> = [];
        try {
          let viewport = flux.getStoreState('OpenCanvasViewportStore').viewport;
          unsubscribes.push(
            flux.getStore('OpenCanvasViewportStore').onChange((state) => {
              if (state.viewport === viewport) return;
              viewport = state.viewport;
              callback();
            })
          );
          unsubscribes.push(
            flux.getStore('StyleBlockStore').onChange(callback)
          );
          unsubscribes.push(
            flux.getStore('CssVariablesStore').onChange(callback)
          );
        } catch (error) {
          unsubscribes.forEach((unsubscribe) => unsubscribe());
          throw error;
        }

        return () => {
          unsubscribes.forEach((unsubscribe) => unsubscribe());
        };
      },
      [flux]
    );
  const startOpenCanvasContainment = React.useCallback(
    (popover: OverlayElement, frame: HTMLElement | null) => {
      clearOpenCanvasContainment();
      if (frame) {
        openCanvasContainmentCleanup.current = containPopoverInOpenCanvasFrame(
          popover,
          frame,
          subscribeToOpenCanvasContainmentUpdates
        );
      }
    },
    [clearOpenCanvasContainment, subscribeToOpenCanvasContainmentUpdates]
  );

  // Hiding always clears `shownElement` in the same step. Leaving it set would
  // have the ref claim a closed element is still shown, and the short-circuit
  // below would then suppress the next legitimate show.
  const removeGhostAnchor = React.useCallback(() => {
    shownElement.current?.removeAttribute(POPOVER_GHOST_SOURCED_ATTRIBUTE);
    designerPopoverRef.current?.removeAttribute(
      POPOVER_GHOST_SOURCED_ATTRIBUTE
    );
    ghostAnchor.current?.remove();
    ghostAnchor.current = null;
  }, [designerPopoverRef]);
  // Hiding removes the ghost outright, so the marker attribute leaves with it
  // and only ever needs to be added or dropped while the ghost is still live.
  const syncGhostMarker = React.useCallback(() => {
    const ghost = ghostAnchor.current;
    if (!ghost) return;

    const element = designerPopoverRef.current;
    if (element && usesImplicitPopoverAnchor(element)) {
      ghost.setAttribute(POPOVER_GHOST_ANCHORED_ATTRIBUTE, '');
    } else {
      ghost.removeAttribute(POPOVER_GHOST_ANCHORED_ATTRIBUTE);
    }
  }, [designerPopoverRef]);
  const clearRetainedSource = React.useCallback(() => {
    retainedSource.current = null;
    if (designerPopoverRef.current) {
      designerPopoverRef.current.__webflowPopoverToolbarSource = null;
    }
  }, [designerPopoverRef]);
  const hideAndClear = React.useCallback(() => {
    hidePopoverSafely(designerPopoverRef.current);
    clearOpenCanvasContainment();
    clearRetainedSource();
    removeGhostAnchor();
    shownElement.current = null;
  }, [
    clearOpenCanvasContainment,
    clearRetainedSource,
    designerPopoverRef,
    removeGhostAnchor,
  ]);
  const selectionHide = React.useCallback(() => {
    hideAndClear();
    descendantHides.current.forEach((hide) => hide());
  }, [hideAndClear]);
  const registerDescendant = React.useCallback((hide: () => void) => {
    descendantHides.current.add(hide);
    return () => descendantHides.current.delete(hide);
  }, []);
  const handleNativeToggle = React.useCallback(
    (event: Event) => {
      if ((event as Event & {newState?: string}).newState !== 'closed') {
        return;
      }
      removeGhostAnchor();
      clearOpenCanvasContainment();
      shownElement.current = null;
    },
    [clearOpenCanvasContainment, removeGhostAnchor]
  );

  const selectionShow = React.useCallback(
    (source?: Element, onReady?: () => void) => {
      if (
        parentController &&
        !parentController.selectionShow(undefined, () =>
          selectionShowRef.current?.(source, onReady)
        )
      ) {
        hideAndClear();
        return false;
      }
      if (forcedClosed.current) {
        hideAndClear();
        return false;
      }
      const element = designerPopoverRef.current;
      if (!element) {
        return false;
      }
      const showPopover = element.showPopover;
      if (!showPopover) {
        return false;
      }
      const showSource =
        anchorStrategy === 'trigger'
          ? (source ?? retainedSource.current)
          : undefined;
      const owningFrame = element.closest<HTMLElement>(
        OPEN_CANVAS_FRAME_SELECTOR
      );
      const isOpenCanvas = Boolean(owningFrame);
      if (shownElement.current === element) {
        if (
          showSource &&
          isOpenCanvas &&
          element.hasAttribute(POPOVER_GHOST_SOURCED_ATTRIBUTE)
        ) {
          try {
            hidePopoverSafely(element);
            showPopoverKeepingHostPageFocus(element, () =>
              showPopover.call(element, {source: showSource})
            );
            removeGhostAnchor();
            startOpenCanvasContainment(element, owningFrame);
          } catch {
            removeGhostAnchor();
            clearOpenCanvasContainment();
            shownElement.current = null;
            return false;
          }
          retainedSource.current = showSource;
          element.__webflowPopoverToolbarSource = showSource;
          shownElement.current = element;
          setPopoverShownVersion((version) => version + 1);
          notifyReady(onReady);
          return true;
        } else {
          if (showSource) {
            retainedSource.current = showSource;
            element.__webflowPopoverToolbarSource = showSource;
          }
          startOpenCanvasContainment(element, owningFrame);
          notifyReady(onReady);
          return true;
        }
      }
      const pendingShows = pendingSelectionShows.current;
      try {
        const authoredTrigger =
          anchorStrategy === 'trigger'
            ? findFirstVisiblePopoverTrigger(
                element,
                owningFrame ?? element.ownerDocument,
                owningFrame ?? undefined
              )
            : null;
        const ghostCandidate =
          showSource ||
          authoredTrigger ||
          (anchorStrategy === 'none' && !owningFrame)
            ? null
            : ghostAnchor.current?.isConnected &&
                (!owningFrame ||
                  ghostAnchor.current.parentElement === owningFrame)
              ? ghostAnchor.current
              : createPopoverGhostAnchor(element, owningFrame ?? undefined);
        const popoverSource = showSource ?? authoredTrigger ?? ghostCandidate;
        if (authoredTrigger) {
          removeGhostAnchor();
        }
        const isGhostSourced =
          ghostCandidate !== null &&
          popoverSource === ghostCandidate &&
          ghostCandidate.hasAttribute(POPOVER_GHOST_ANCHOR_ATTRIBUTE);
        if (isGhostSourced) {
          element.setAttribute(POPOVER_GHOST_SOURCED_ATTRIBUTE, '');
          ghostAnchor.current = ghostCandidate;
          syncGhostMarker();
        } else {
          removeGhostAnchor();
        }
        showPopoverKeepingHostPageFocus(element, () =>
          popoverSource
            ? showPopover.call(element, {source: popoverSource})
            : showPopover.call(element)
        );
        startOpenCanvasContainment(element, owningFrame);
        retainedSource.current = showSource ?? null;
        element.__webflowPopoverToolbarSource = showSource ?? null;
        shownElement.current = element;
        pendingSelectionShows.current = [];
      } catch {
        removeGhostAnchor();
        // Older browsers and unsupported native Popover implementations fail safely.
        return false;
      }
      setPopoverShownVersion((version) => version + 1);
      pendingShows.forEach(notifyReady);
      notifyReady(onReady);
      return true;
    },
    [
      anchorStrategy,
      clearOpenCanvasContainment,
      designerPopoverRef,
      hideAndClear,
      notifyReady,
      parentController,
      removeGhostAnchor,
      syncGhostMarker,
      startOpenCanvasContainment,
    ]
  );

  React.useEffect(() => {
    if (popoverShownVersion === 0) return;

    dispatch({type: OVERLAY_SHOWN, payload: undefined});
  }, [dispatch, popoverShownVersion]);

  const selectionShowWithPending = React.useCallback(
    (source?: Element, onReady?: () => void) => {
      if (!designerPopoverRef.current) {
        if (onReady) {
          pendingSelectionShows.current.push(onReady);
        }
        return false;
      }
      return selectionShow(source, onReady);
    },
    [designerPopoverRef, selectionShow]
  );
  selectionShowRef.current = selectionShowWithPending;
  const controller = React.useMemo(
    () => ({
      selectionShow: selectionShowWithPending,
      selectionHide,
      registerDescendant,
    }),
    [registerDescendant, selectionHide, selectionShowWithPending]
  );

  useIsomorphicLayoutEffect(() => {
    if (!parentController) return;
    return parentController.registerDescendant(selectionHide);
  }, [parentController, selectionHide]);

  const applyCommand = React.useCallback(
    (command: PopoverCommand) => {
      // IX3 drives these at the DOM level on published pages and in preview,
      // where `enabled` is false and this listener isn't even attached. In the
      // canvas, visibility follows selection — so ignore them rather than let a
      // stray event yank a popover open behind the author's back.
      if (
        command.type === POPOVER_ANIMATION_OPEN_COMMAND ||
        command.type === POPOVER_ANIMATION_CLOSE_COMMAND
      ) {
        return;
      }

      if (command.type === 'set-forced-closed') {
        forcedClosed.current = command.forcedClosed;
        if (!command.forcedClosed) return;
        setExplicitlyOpenedSelection(null);
        setForcedClosedSelection(
          selectedNativeIdPath ? [...selectedNativeIdPath] : null
        );
      } else if (command.type === 'show') {
        forcedClosed.current = false;
        setForcedClosedSelection(null);
        retainedSource.current = command.source ?? null;
        if (designerPopoverRef.current) {
          designerPopoverRef.current.__webflowPopoverToolbarSource =
            command.source ?? null;
        }
        const isSelectionDriven =
          selectedNativeIdPath !== undefined &&
          isPopoverSelectedForPath(
            data,
            elementId,
            popoverNativeIdPath,
            selectedNativeIdPath
          );
        const initiatingNativeIdPath =
          command.nativeIdPath ?? selectedNativeIdPath;
        setExplicitlyOpenedSelection(
          initiatingNativeIdPath && !isSelectionDriven
            ? [...initiatingNativeIdPath]
            : null
        );
        controller.selectionShow(command.source);
        return;
      } else if (command.type === 'selection-show') {
        controller.selectionShow();
        return;
      } else {
        forcedClosed.current = true;
        setExplicitlyOpenedSelection(null);
        setForcedClosedSelection(
          selectedNativeIdPath ? [...selectedNativeIdPath] : null
        );
      }

      controller.selectionHide();
    },
    [controller, data, elementId, popoverNativeIdPath, selectedNativeIdPath]
  );

  useIsomorphicLayoutEffect(() => {
    if (!enabled || !designerPopoverElement) return;
    return listenForPopoverCommands(designerPopoverElement, applyCommand);
  }, [applyCommand, designerPopoverElement, enabled]);

  useIsomorphicLayoutEffect(() => {
    if (!enabled || !designerPopoverElement) return;
    const element = designerPopoverElement;
    element.addEventListener('toggle', handleNativeToggle);
    return () => element.removeEventListener('toggle', handleNativeToggle);
  }, [designerPopoverElement, enabled, handleNativeToggle]);

  useIsomorphicLayoutEffect(() => {
    if (!enabled) {
      removeGhostAnchor();
      clearOpenCanvasContainment();
      return;
    }
    return () => {
      removeGhostAnchor();
      clearOpenCanvasContainment();
    };
  }, [clearOpenCanvasContainment, enabled, removeGhostAnchor]);

  // `position-anchor` is authored in the Style Panel, which rewrites the canvas
  // stylesheet rather than this element's props, so the marker is re-read on
  // every render instead of being decided once when the ghost is adopted.
  useIsomorphicLayoutEffect(() => {
    syncGhostMarker();
  });
  const observedSelectedNativeIdPath = useSelectedNativeIdPath(
    selectedNativeIdPath,
    enabled && explicitlyOpenedSelection !== null
  );

  useIsomorphicLayoutEffect(() => {
    const currentSelectedNativeIdPath = observedSelectedNativeIdPath;
    if (!enabled || currentSelectedNativeIdPath === undefined) {
      if (explicitlyOpenedSelection) {
        setExplicitlyOpenedSelection(null);
        forcedClosed.current = false;
        hideAndClear();
      }
      if (!enabled) {
        clearRetainedSource();
        setForcedClosedSelection(null);
      }
      return;
    }

    if (explicitlyOpenedSelection) {
      if (
        areNativeIdPathsEqual(
          explicitlyOpenedSelection,
          currentSelectedNativeIdPath
        )
      ) {
        controller.selectionShow();
        return;
      }

      const isSelectedPopoverOrDescendant = isPopoverSelectedForPath(
        data,
        elementId,
        popoverNativeIdPath,
        currentSelectedNativeIdPath
      );

      if (!isSelectedPopoverOrDescendant) {
        setExplicitlyOpenedSelection(null);
        forcedClosed.current = false;
        skipNextSelectionHide.current = true;
        clearRetainedSource();
        hideAndClear();
        return;
      }

      controller.selectionShow();
      if (
        !areNativeIdPathsEqual(
          selectedNativeIdPath ?? [],
          currentSelectedNativeIdPath
        )
      ) {
        return;
      }

      clearRetainedSource();
      setExplicitlyOpenedSelection(null);
      return;
    }

    const isSelectedPopoverOrDescendant = isPopoverSelectedForPath(
      data,
      elementId,
      popoverNativeIdPath,
      currentSelectedNativeIdPath
    );

    if (skipNextSelectionHide.current) {
      skipNextSelectionHide.current = false;
      return;
    }

    if (forcedClosed.current) {
      if (
        forcedClosedSelection &&
        areNativeIdPathsEqual(
          forcedClosedSelection,
          currentSelectedNativeIdPath
        )
      ) {
        hideAndClear();
        return;
      }

      forcedClosed.current = false;
      setForcedClosedSelection(null);
      if (
        isPopoverSelectedForPath(
          data,
          elementId,
          popoverNativeIdPath,
          currentSelectedNativeIdPath
        )
      ) {
        controller.selectionShow();
        return;
      }
      clearRetainedSource();
      hideAndClear();
      return;
    }

    // A hide command only survives while the selection stays inside this tree.
    if (!isSelectedPopoverOrDescendant) {
      forcedClosed.current = false;
      setForcedClosedSelection(null);
      clearRetainedSource();
    }

    if (isSelectedPopoverOrDescendant && !forcedClosed.current) {
      controller.selectionShow();
      return;
    }

    selectionHide();
  }, [
    enabled,
    popoverNativeIdPath,
    hideAndClear,
    selectedNativeIdPath,
    observedSelectedNativeIdPath,
    explicitlyOpenedSelection,
    setExplicitlyOpenedSelection,
    controller,
    data,
    elementId,
    selectionHide,
    clearRetainedSource,
    forcedClosedSelection,
  ]);

  return controller;
};

type DesignerOverlayTargetProps = {
  data: EvaluatedNodeData;
  elementId: string;
  environmentContext: Parameters<typeof computeNativeId>[1];
  nativeIdPath: readonly string[];
  selectedNativeIdPath: readonly string[] | undefined;
  enabled: boolean;
  anchorStrategy?: OverlayAnchorStrategy;
  renderTag: (
    ref: React.RefCallback<OverlayElement> | undefined
  ) => React.ReactElement;
};

export const DesignerOverlayTarget = ({
  data,
  elementId,
  environmentContext,
  nativeIdPath,
  selectedNativeIdPath,
  enabled,
  anchorStrategy = 'trigger',
  renderTag,
}: DesignerOverlayTargetProps) => {
  const designerPopoverRef = React.useRef<OverlayElement | null>(null);
  const [designerPopoverElement, setDesignerPopoverElement] =
    React.useState<OverlayElement | null>(null);
  const setDesignerPopoverRef = React.useCallback(
    (element: OverlayElement | null) => {
      if (!element && designerPopoverRef.current) {
        designerPopoverRef.current.__webflowPopoverToolbarSource = null;
      }
      designerPopoverRef.current = element;
      setDesignerPopoverElement(element);
    },
    []
  );
  const parentController = React.useContext(OverlayOwnershipContext);
  const nativeIdPathKey = nativeIdPath.join('\0');
  const popoverNativeIdPath = React.useMemo(
    () => nativeIdPath.concat(computeNativeId(elementId, environmentContext)),
    [elementId, environmentContext, nativeIdPathKey]
  );
  const controller = useDesignerOverlayVisibility({
    data,
    elementId,
    popoverNativeIdPath,
    selectedNativeIdPath,
    designerPopoverRef,
    designerPopoverElement,
    enabled,
    anchorStrategy,
    parentController,
  });

  return (
    <OverlayOwnershipContext.Provider value={controller}>
      {renderTag(enabled ? setDesignerPopoverRef : undefined)}
    </OverlayOwnershipContext.Provider>
  );
};
